// ── canonCeremony.js ────────────────────────────────────────────────────────
// Detects the moment a Canon (franchise tracker, curator_lists kind='universe')
// crosses from <100% to =100% aggregate progress as a RESULT of a single show's
// status just changing to 'completed', and persists the canon_keepsakes row that
// makes that moment permanent + idempotent. Pure data logic — no React, no DOM —
// so useWatchlist.jsx can call it fire-and-forget from setStatus without pulling
// UI concerns into the hook.
//
// Progress is never stored (see lib/universes.js) so "just crossed 100%" isn't a
// single row flip to watch — we recompute the WHOLE canon's aggregate twice: once
// with this show's PRE-change state (its status/progress a moment ago) and once
// with its POST-change state (completed, final episode), holding every other show
// in the canon fixed at its current watchlist state. If pre < 100% <= post, this
// show's completion is what tipped the canon over — fire once, ever, per user+canon.
import { supabase } from './supabase'
import { fetchUniverses, showProgress } from './universes'
import { labsWrite } from './labs'

let canonsPromise = null
// Lazy + cached at module scope: every show-completion in the app calls this,
// but the canon roster (curator_lists kind='universe') changes rarely, so one
// fetch per session (not per completion) is enough. Reset only on hard reload.
function loadCanons() {
  if (!canonsPromise) canonsPromise = fetchUniverses().then(r => (r && r.error) ? [] : (r || []))
  return canonsPromise
}

function aggregateFor(canonShows, wlLike) {
  let watched = 0, total = 0
  for (const show of canonShows) {
    const p = showProgress(show, wlLike)
    watched += p.watched
    total += p.total
  }
  return { watched, total, pct: total ? (watched / total) : 0 }
}

// wlLike shape matches what showProgress()/universeProgress() expect: { statuses, progress }
function overrideOneShow(wl, showId, status, progress) {
  return {
    statuses: { ...wl.statuses, [showId]: status },
    progress: { ...wl.progress, [showId]: progress },
  }
}

// Called from useWatchlist.setStatus right after a show flips to 'completed'.
// `prev` carries the show's state just BEFORE this change (undefined status /
// prior progress) so we can reconstruct the "a moment ago" aggregate honestly —
// this is what lets a user who was ALREADY 100% before this feature shipped
// never get blasted retroactively: their "before" snapshot is also 100%, so
// pre>=100 short-circuits below and nothing fires.
export async function checkCanonCompletions({ user, show, showId, finalEp, prevStatus, prevProgress, wl, onFire }) {
  if (!user?.id || !show?.id || !finalEp) return
  let canons
  try { canons = await loadCanons() } catch { return }
  const memberOf = canons.filter(c => (c.shows || []).some(s => s.id === show.id))
  if (!memberOf.length) return

  for (const canon of memberOf) {
    try {
      const before = overrideOneShow(wl, showId, prevStatus || null, prevProgress || null)
      const after  = overrideOneShow(wl, showId, 'completed', finalEp)
      const preAgg  = aggregateFor(canon.shows, before)
      const postAgg = aggregateFor(canon.shows, after)
      if (postAgg.total === 0) continue
      const wasComplete = preAgg.total > 0 && preAgg.pct >= 1
      const nowComplete = postAgg.pct >= 1
      if (wasComplete || !nowComplete) continue // not a fresh crossing

      // Idempotent: a row already existing means this canon was already recorded
      // (either a genuine earlier completion, or — the retroactive-blast guard —
      // a backfill/replay). Either way, don't fire again.
      const { data: existing } = await supabase
        .from('canon_keepsakes').select('id').eq('user_id', user.id).eq('canon_id', canon.id).maybeSingle()
      if (existing) continue

      const totalMinutes = canon.shows.reduce((sum, s) => {
        const rt = (s.episode_run_time && s.episode_run_time[0]) || 0
        const eps = s.number_of_episodes || 0
        return sum + rt * eps
      }, 0)
      const totalEpisodes = canon.shows.reduce((sum, s) => sum + (s.number_of_episodes || 0), 0)
      const totalSeasons = canon.shows.reduce((sum, s) => sum + (s.number_of_seasons || 0), 0)

      let startedAt = null
      try {
        const { data: rows } = await supabase.from('watchlist').select('added_at')
          .eq('user_id', user.id).in('show_id', canon.shows.map(s => s.id)).order('added_at', { ascending: true }).limit(1)
        startedAt = rows && rows[0] ? rows[0].added_at : null
      } catch { /* best-effort */ }

      const snapshot = canon.shows.map(s => ({ id: s.id, name: s.name, poster_path: s.poster_path || null }))

      // Labs mode (dry-run): compute + fire the real ceremony data, but skip
      // persisting the keepsake row so Jason can replay the cinematic on a real
      // account without polluting canon_keepsakes. Labs OFF = unchanged real insert.
      const { error: insErr } = await labsWrite(`canon_keepsakes insert (${canon.title})`, () =>
        supabase.from('canon_keepsakes').insert({
          user_id: user.id, canon_id: canon.id, canon_name: canon.title,
          show_count: canon.shows.length, total_episodes: totalEpisodes, total_minutes: totalMinutes,
          started_at: startedAt, show_snapshot: snapshot,
        })
      )
      // A unique-violation here means another code path (or a race) already
      // inserted this canon's keepsake a beat ago — treat it as "already fired",
      // not an error, and skip the cinematic rather than risk a double-fire.
      if (insErr && insErr.code !== '23505') { console.error('[canonCeremony] keepsake insert failed', insErr); continue }
      if (insErr) continue

      // Other canons the user hasn't finished yet — real suggestions for the
      // ceremony's closing scene, not hardcoded placeholders. Best-effort: a
      // failed lookup just means the closing scene falls back to an empty list.
      let otherCanons = []
      try {
        const { data: done } = await supabase.from('canon_keepsakes').select('canon_id').eq('user_id', user.id)
        const doneIds = new Set([...(done || []).map(r => r.canon_id), canon.id])
        otherCanons = canons.filter(c => !doneIds.has(c.id)).slice(0, 3).map(c => ({ id: c.id, title: c.title }))
      } catch { /* best-effort */ }

      onFire?.({ canon, showCount: canon.shows.length, totalEpisodes, totalSeasons, totalMinutes, startedAt, snapshot, finalTitle: show.name, otherCanons, user })
    } catch (err) {
      console.error('[canonCeremony] check failed for', canon?.title, err)
    }
  }
}
